import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { adminAPI, companyAPI } from '../services/api';
import Icon from './Icon';
import BikePassportModal from './BikePassportModal';
import '../styles/Admin.css';

function AdminBikes({ user, onLogout, onToggleDarkMode, darkMode = true }) {
  const MAX_IMAGE_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB cap for batch uploads
  
  // Check if current user can edit a bike (only own bikes while not activated)
  const canEditBike = (bike) => {
    // Super admins can always edit
    if (user?.role === 'super_admin') return { canEdit: true };
    
    // Check if user added this bike
    if (bike.added_by_user_id && bike.added_by_user_id !== user?.id) {
      return { canEdit: false, reason: 'You can only edit bikes you personally added' };
    }
    
    // Check if bike is activated (user registered with VIN)
    if (bike.is_activated) {
      return { canEdit: false, reason: 'This bike has been activated by a user and can no longer be edited' };
    }
    
    return { canEdit: true };
  };
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showServiceHistoryModal, setShowServiceHistoryModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showPassportModal, setShowPassportModal] = useState(false);
  const [passportBike, setPassportBike] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [openActionsBikeId, setOpenActionsBikeId] = useState(null);
  const [actionsMenuPosition, setActionsMenuPosition] = useState(null);
  const [currentBike, setCurrentBike] = useState(null);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [formData, setFormData] = useState({
    vin: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    owner_id: '' // Optional: assign to existing user
  });
  const [blockReason, setBlockReason] = useState('');
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBikes, setTotalBikes] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Detail modal state
  const [detailTab, setDetailTab] = useState('info');
  const [bikeImages, setBikeImages] = useState([]);
  const [bikeComments, setBikeComments] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [addImages, setAddImages] = useState([]);
  const [addProfileSelection, setAddProfileSelection] = useState('');
  const [addImageError, setAddImageError] = useState('');
  const [editImages, setEditImages] = useState([]);
  const [editExistingImages, setEditExistingImages] = useState([]);
  const [editProfileSelection, setEditProfileSelection] = useState('');
  const [editImageError, setEditImageError] = useState('');
  const [loadingEditImages, setLoadingEditImages] = useState(false);
  const actionsMenuRef = useRef(null);
  const actionsAnchorRef = useRef(null);

  const closeActionsMenu = () => {
    setOpenActionsBikeId(null);
    setActionsMenuPosition(null);
    actionsAnchorRef.current = null;
  };

  useEffect(() => {
    fetchCompanyInfo();
    fetchCompanyUsers();
  }, []);

  useEffect(() => {
    fetchBikes();
  }, [currentPage, pageSize, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openActionsBikeId) return undefined;

    const handleOutsideClick = (event) => {
      const menuEl = actionsMenuRef.current;
      const anchorEl = actionsAnchorRef.current;
      if (menuEl && menuEl.contains(event.target)) return;
      if (anchorEl && anchorEl.contains(event.target)) return;
      closeActionsMenu();
    };

    const handleViewportChange = () => {
      closeActionsMenu();
    };

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [openActionsBikeId]);

  const fetchCompanyUsers = async () => {
    try {
      const response = await companyAPI.getCompanyUsers();
      setCompanyUsers(response.data.users || []);
    } catch (err) {
      console.log('Could not fetch company users');
    }
  };

  const fetchBikes = async (overrides = {}) => {
    const nextPage = overrides.page || currentPage;
    const nextPageSize = overrides.perPage || pageSize;
    try {
      setLoading(true);
      const response = await adminAPI.getAllBikes({
        page: nextPage,
        per_page: nextPageSize,
        q: searchTerm.trim() || undefined
      });
      setBikes(response.data.bikes || []);
      setTotalBikes(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
      if (overrides.page) {
        setCurrentPage(overrides.page);
      }
      if (overrides.perPage) {
        setPageSize(overrides.perPage);
      }
      setError('');
    } catch (err) {
      setError('Failed to load bikes');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await companyAPI.getCompanyInfo();
      setCompanyInfo(response.data.company);
    } catch (err) {
      console.log('Company info not available');
    }
  };

  const validateAndSetImages = (files, setImagesFn, setProfileFn, setLocalErrorFn, keepExistingProfile = false) => {
    if (!files || files.length === 0) return;

    let totalSize = 0;
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setLocalErrorFn('Only image files can be uploaded');
        return;
      }
      totalSize += file.size;
    }

    if (totalSize > MAX_IMAGE_TOTAL_SIZE) {
      setLocalErrorFn('Total images size must be at most 20MB');
      return;
    }

    setLocalErrorFn('');
    setImagesFn(files);
    if (files.length > 0 && !keepExistingProfile) {
      setProfileFn(`new-0`);
    }
  };

  const uploadImagesWithProfile = async (bikeId, files, profileSelection) => {
    if (!files || files.length === 0) {
      if (profileSelection?.startsWith('existing-')) {
        const existingId = parseInt(profileSelection.replace('existing-', ''), 10);
        if (existingId) {
          await adminAPI.setBikeProfileImage(bikeId, existingId);
        }
      }
      return;
    }

    const uploadedImageIds = [];
    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append('image', files[i]);
      const response = await adminAPI.uploadBikeImage(bikeId, formData);
      const uploaded = response?.data?.image;
      if (uploaded?.id) {
        uploadedImageIds.push(uploaded.id);
        if (profileSelection === `new-${i}`) {
          await adminAPI.setBikeProfileImage(bikeId, uploaded.id);
        }
      }
    }

    if (profileSelection?.startsWith('existing-')) {
      const existingId = parseInt(profileSelection.replace('existing-', ''), 10);
      if (existingId) {
        await adminAPI.setBikeProfileImage(bikeId, existingId);
        return;
      }
    }

    if (profileSelection?.startsWith('new-')) {
      const index = parseInt(profileSelection.replace('new-', ''), 10);
      const selectedId = uploadedImageIds[index];
      if (selectedId) {
        await adminAPI.setBikeProfileImage(bikeId, selectedId);
        return;
      }
    }

    // Fallback: if nothing explicitly chosen, set first uploaded as profile
    if (uploadedImageIds.length > 0) {
      await adminAPI.setBikeProfileImage(bikeId, uploadedImageIds[0]);
    }
  };

  const getFilesTotalSize = (files) => files.reduce((sum, file) => sum + (file?.size || 0), 0);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      if (getFilesTotalSize(addImages) > MAX_IMAGE_TOTAL_SIZE) {
        setAddImageError('Total images size must be at most 20MB');
        return;
      }

      const response = await adminAPI.addBike(formData);
      const newBikeId = response?.data?.bike?.id;

      if (newBikeId) {
        await uploadImagesWithProfile(newBikeId, addImages, addProfileSelection);
      }

      setSuccess('Bike added successfully!');
      setShowAddModal(false);
      setFormData({ vin: '', brand: '', model: '', year: new Date().getFullYear() });
      setAddImages([]);
      setAddProfileSelection('');
      setAddImageError('');
      fetchBikes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add bike');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      if (getFilesTotalSize(editImages) > MAX_IMAGE_TOTAL_SIZE) {
        setEditImageError('Total images size must be at most 20MB');
        return;
      }

      // Only year can be updated, brand and model are locked
      await adminAPI.updateBike(currentBike.id, {
        year: formData.year
      });

      if (currentBike?.id) {
        await uploadImagesWithProfile(currentBike.id, editImages, editProfileSelection);
      }

      setSuccess('Bike updated successfully!');
      setShowEditModal(false);
      setEditImages([]);
      setEditExistingImages([]);
      setEditProfileSelection('');
      setEditImageError('');
      fetchBikes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update bike');
    }
  };

  const handleDelete = async (bikeId) => {
    if (!window.confirm('Are you sure you want to delete this bike? This will also delete all service records.')) {
      return;
    }
    
    try {
      const res = await adminAPI.deleteBike(bikeId);
      const msg = res.data?.removed
        ? 'Bike removed from company.'
        : 'Bike deleted successfully!';
      setSuccess(msg);
      setError('');
      fetchBikes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to delete bike';
      setError(msg);
    }
  };

  const openEditModal = async (bike) => {
    closeActionsMenu();
    setCurrentBike(bike);
    setFormData({
      vin: bike.vin,
      brand: bike.brand,
      model: bike.model,
      year: bike.year
    });
    setEditImages([]);
    setEditExistingImages([]);
    setEditProfileSelection('');
    setEditImageError('');
    setLoadingEditImages(true);
    setShowEditModal(true);
    try {
      const imagesRes = await adminAPI.getBikeImages(bike.id);
      const images = imagesRes.data.images || [];
      setEditExistingImages(images);
      const currentProfile = images.find(img => img.is_profile);
      if (currentProfile?.id) {
        setEditProfileSelection(`existing-${currentProfile.id}`);
      }
    } catch (err) {
      console.error('Failed to load bike images', err);
      setEditExistingImages([]);
    } finally {
      setLoadingEditImages(false);
    }
  };

  const viewServiceHistory = async (bike) => {
    closeActionsMenu();
    setCurrentBike(bike);
    setShowServiceHistoryModal(true);
    setLoadingServices(true);
    try {
      const response = await adminAPI.getBikeServices(bike.id);
      setServiceHistory(response.data.services);
      setError('');
    } catch (err) {
      setError('Failed to load service history');
      setServiceHistory([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const openBlockModal = (bike) => {
    closeActionsMenu();
    setCurrentBike(bike);
    setBlockReason('');
    setShowBlockModal(true);
  };

  const openPassportModal = (bike) => {
    closeActionsMenu();
    setPassportBike({ id: bike.id, brand: bike.brand, model: bike.model });
    setShowPassportModal(true);
  };

  // Detail modal functions
  const openDetailModal = async (bike) => {
    closeActionsMenu();
    setCurrentBike(bike);
    setShowDetailModal(true);
    setDetailTab('info');
    setLoadingDetail(true);
    
    try {
      const [imagesRes, commentsRes] = await Promise.all([
        adminAPI.getBikeImages(bike.id),
        adminAPI.getBikeComments(bike.id)
      ]);
      setBikeImages(imagesRes.data.images || []);
      setBikeComments(commentsRes.data.comments || []);
    } catch (err) {
      console.error('Error loading bike details', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const getBikeImageUrl = (image, bikeIdOverride = null) => {
    if (!image) return '';
    const bikeId = bikeIdOverride || currentBike?.id;
    return image.file_url || image.file_path || (image.filename ? `/api/user/bikes/${bikeId}/images/${image.filename}` : '');
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await adminAPI.addBikeComment(currentBike.id, {
        comment_text: newComment,
        category: newCategory
      });
      setBikeComments([response.data.comment, ...bikeComments]);
      setNewComment('');
      setNewCategory('general');
      setSuccess('Comment added!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Only image uploads are allowed');
      return;
    }

    if (file.size > MAX_IMAGE_TOTAL_SIZE) {
      setError('File size must be at most 20MB');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await adminAPI.uploadBikeImage(currentBike.id, formData);
      setBikeImages([response.data.image, ...bikeImages]);
      setSuccess('Image uploaded!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSetProfileImage = async (imageId) => {
    try {
      await adminAPI.setBikeProfileImage(currentBike.id, imageId);
      setBikeImages(bikeImages.map(img => ({
        ...img,
        is_profile: img.id === imageId
      })));
      setSuccess('Profile image set!');
      fetchBikes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set profile image');
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await adminAPI.deleteBikeImage(currentBike.id, imageId);
      setBikeImages(bikeImages.filter(img => img.id !== imageId));
      setSuccess('Image deleted!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete image');
    }
  };

  const getCategories = (darkMode = true) => ({
    general: { iconName: 'chat', label: 'General', darkMode },
    maintenance: { iconName: 'service', label: 'Maintenance', darkMode },
    issue: { iconName: 'warning', label: 'Issue', darkMode },
    modification: { iconName: 'edit', label: 'Modification', darkMode },
    inspection: { iconName: 'view', label: 'Inspection', darkMode }
  });

  const handleBlockBike = async (e) => {
    e.preventDefault();
    if (!blockReason.trim()) {
      setError('Block reason is required');
      return;
    }
    try {
      await companyAPI.blockBike(currentBike.id, blockReason);
      setSuccess('Bike blocked. A support ticket has been created.');
      setShowBlockModal(false);
      setBlockReason('');
      fetchBikes();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to block bike');
    }
  };

  if (loading && bikes.length === 0) {
    return <div className="loading">Loading bikes...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2 style={{color: '#ffffff'}}>Bike Management</h2>
        <div>
          {(user?.role === 'company_admin' || user?.role === 'admin' || user?.role === 'super_admin' || (user?.role === 'technician' && companyInfo?.technician_can_add_bikes)) && (
            <button 
              onClick={() => {
                setShowAddModal(true);
                setAddImages([]);
                setAddProfileSelection('');
                setAddImageError('');
              }} 
              className="btn btn-primary"
            >
              + Add New Bike
            </button>
          )}
        </div>
      </div>

      {companyInfo && (
        <div style={{marginBottom: '15px', padding: '12px 16px', backgroundColor: 'rgba(61, 90, 32, 0.08)', border: '1px solid rgba(61, 90, 32, 0.2)', borderRadius: '12px', fontSize: '14px', color: 'rgba(255,255,255,0.8)'}}>
          <strong style={{color: '#3d5a20'}}>Company:</strong> {companyInfo.name}
          {companyInfo.email && <span> | {companyInfo.email}</span>}
          {companyInfo.phone && <span> | {companyInfo.phone}</span>}
        </div>
      )}

      <div style={{marginBottom: '15px'}}>
        <label style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
          Search Bikes
        </label>
        <input
          type="text"
          placeholder="Search by frame number, brand, model, or owner name..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'var(--bg-overlay-medium)',
            color: '#ffffff',
            fontSize: '14px'
          }}
        />
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="card">
        {bikes.length === 0 ? (
          <p style={{padding: '20px', textAlign: 'center', color: 'var(--color-gray)'}}>
            {searchTerm ? 'No bikes found matching your search.' : 'No bikes found.'}
          </p>
        ) : (
          <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Customer Surname</th>
                <th>Firstname</th>
                <th>Bike Brand</th>
                <th>Bike Model</th>
                <th>Frame Number</th>
                <th>Year</th>
                <th>Status</th>
                <th>Services</th>
                {bikes.some(b => b.company_registrations && b.company_registrations.length > 0) && (
                  <th>Company Registrations</th>
                )}
                <th style={{ minWidth: '200px', width: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bikes.map(bike => (
                <tr key={bike.id}>
                  <td data-label="Surname">{bike.owner_surname || 'N/A'}</td>
                  <td data-label="Firstname">{bike.owner_firstname || 'N/A'}</td>
                  <td data-label="Brand">{bike.brand}</td>
                  <td data-label="Model">{bike.model}</td>
                  <td data-label="Frame #">{bike.vin}</td>
                  <td data-label="Year">{bike.year}</td>
                  <td data-label="Status">
                    {bike.is_activated ? (
                      <span 
                        style={{
                          background: 'rgba(74, 140, 24, 0.15)',
                          color: '#4A8C18',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}
                        title="User registered with this bike - no longer editable"
                      >
                        Activated
                      </span>
                    ) : (
                      <span 
                        style={{
                          background: 'rgba(255, 193, 7, 0.15)',
                          color: '#ffc107',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}
                        title="Awaiting user registration - can still be edited"
                      >
                        Pending
                      </span>
                    )}
                  </td>
                  <td data-label="Services">
                    <span 
                      onClick={() => viewServiceHistory(bike)}
                      style={{
                        cursor: 'pointer',
                        color: '#4ecdc4',
                        textDecoration: 'underline',
                        fontWeight: '600'
                      }}
                      title="Click to view service history"
                    >
                      {bike.service_count || 0}
                    </span>
                  </td>
                  {bike.company_registrations && bike.company_registrations.length > 0 && (
                    <td data-label="Registrations">
                      <div style={{fontSize: '12px'}}>
                        {bike.company_registrations.filter(reg => reg.is_active).length > 0 ? (
                          <span style={{color: '#4A8C18', fontWeight: 'bold'}}>
                            {bike.company_registrations.filter(reg => reg.is_active).length} active
                          </span>
                        ) : (
                          <span style={{color: '#9e9e9e'}}>No active</span>
                        )}
                        {bike.company_registrations.length > bike.company_registrations.filter(reg => reg.is_active).length && (
                          <span style={{color: 'var(--color-gray)', marginLeft: '5px'}}>
                            ({bike.company_registrations.length - bike.company_registrations.filter(reg => reg.is_active).length} inactive)
                          </span>
                        )}
                      </div>
                    </td>
                  )}
                  <td data-label="Actions" style={{ overflow: 'visible', whiteSpace: 'nowrap', minWidth: '200px' }}>
                    <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                      <button onClick={() => openPassportModal(bike)} className="btn btn-primary btn-sm" title="Bike Passport">
                        <Icon name="badge" color="primary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        Bike Passport
                      </button>
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openActionsBikeId === bike.id) {
                              closeActionsMenu();
                              return;
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            const menuWidth = 160;
                            const menuHeight = 140;
                            const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
                            const preferBelow = rect.bottom + 8 + menuHeight <= window.innerHeight;
                            const top = preferBelow ? rect.bottom + 6 : Math.max(8, rect.top - menuHeight - 6);
                            setActionsMenuPosition({ top, left });
                            setOpenActionsBikeId(bike.id);
                            actionsAnchorRef.current = e.currentTarget;
                          }}
                          title="Edit, Block, Delete"
                          style={{ padding: '6px 10px', minWidth: '36px' }}
                        >
                          <Icon name="more_vert" color="secondary" darkMode={darkMode} size={20} style={{ display: 'block', margin: 0 }} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {totalBikes > 0 && (
          <div style={{
            padding: '10px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            fontSize: '12px',
            color: 'var(--color-gray)'
          }}>
            <div>
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalBikes)} of {totalBikes}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="bikes-page-size">Per page</label>
              <select
                id="bikes-page-size"
                value={pageSize}
                onChange={(e) => {
                  const nextPageSize = Number(e.target.value);
                  setCurrentPage(1);
                  setPageSize(nextPageSize);
                }}
                style={{ padding: '6px 8px', borderRadius: '8px', fontSize: '12px' }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
              >
                Prev
              </button>
              <span>Page {currentPage}/{totalPages}</span>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {openActionsBikeId && actionsMenuPosition && (() => {
        const selectedBike = bikes.find(b => b.id === openActionsBikeId);
        if (!selectedBike) return null;
        const editStatus = canEditBike(selectedBike);
        return ReactDOM.createPortal(
          <div
            ref={actionsMenuRef}
            style={{
              position: 'fixed',
              top: `${actionsMenuPosition.top}px`,
              left: `${actionsMenuPosition.left}px`,
              width: '160px',
              zIndex: 100200,
              background: darkMode ? '#1a1a2e' : '#ffffff',
              border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
              borderRadius: '10px',
              boxShadow: '0 10px 24px rgba(0,0,0,0.5)',
              padding: '6px'
            }}
          >
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ width: '100%', justifyContent: 'flex-start' }}
              onClick={() => {
                openDetailModal(selectedBike);
              }}
              title="View bike details"
            >
              Details
            </button>
            {editStatus.canEdit && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ width: '100%', justifyContent: 'flex-start' }}
                onClick={() => {
                  openEditModal(selectedBike);
                }}
                title="Edit bike"
              >
                Edit
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm"
              style={{
                width: '100%',
                justifyContent: 'flex-start',
                background: 'rgba(239, 68, 68, 0.15)',
                color: '#f87171',
                border: '1px solid rgba(239, 68, 68, 0.25)'
              }}
              onClick={() => {
                openBlockModal(selectedBike);
              }}
              title="Block bike"
            >
              Block
            </button>
            {(user?.role === 'super_admin' || !selectedBike.is_activated) && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                style={{ width: '100%', justifyContent: 'flex-start' }}
                onClick={() => {
                  handleDelete(selectedBike.id);
                  closeActionsMenu();
                }}
                title={user?.role === 'super_admin' ? 'Permanently delete bike' : 'Remove from company (pending only)'}
              >
                Delete
              </button>
            )}
          </div>,
          document.body
        );
      })()}

      {/* Add Bike Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Bike</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>x</button>
            </div>
            
            <form onSubmit={handleAdd}>
              <div className="form-group">
                <label>Frame Number *</label>
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) => setFormData({...formData, vin: e.target.value})}
                  required
                  maxLength={17}
                  placeholder="Enter frame number"
                />
              </div>

              <div className="form-group">
                <label>Brand *</label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => setFormData({...formData, brand: e.target.value})}
                  required
                  placeholder="e.g., Trek, Specialized"
                />
              </div>

              <div className="form-group">
                <label>Model *</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                  required
                  placeholder="e.g., Domane SL 5"
                />
              </div>

              <div className="form-group">
                <label>Year *</label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                  required
                  min={1900}
                  max={new Date().getFullYear() + 1}
                />
              </div>

              <div className="form-group">
                <label>Assign to User (optional)</label>
                <select
                  value={formData.owner_id}
                  onChange={(e) => setFormData({...formData, owner_id: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- No owner (company bike) --</option>
                  {companyUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstname || ''} {user.surname || ''} ({user.username})
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '6px' }}>
                  Select an existing user to assign this bike to, or leave empty for a company-owned bike.
                </p>
              </div>

              <div className="form-group">
                <label>Images (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => validateAndSetImages(
                    Array.from(e.target.files || []),
                    setAddImages,
                    setAddProfileSelection,
                    setAddImageError
                  )}
                />
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '6px' }}>
                  You can select multiple images, up to 20MB total. Choose which one should be the profile image.
                </p>
                {addImageError && <div className="error">{addImageError}</div>}
                {addImages.length > 0 && (
                  <div style={{ marginTop: '10px', background: 'var(--bg-overlay-light)', padding: '10px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '8px' }}>
                      Selected images total: {(getFilesTotalSize(addImages) / (1024 * 1024)).toFixed(2)} MB / 20 MB
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px' }}>
                      {addImages.map((file, index) => (
                        <label key={file.name + index} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#ffffff' }}>
                          <input
                            type="radio"
                            name="addProfileImage"
                            value={`new-${index}`}
                            checked={addProfileSelection === `new-${index}`}
                            onChange={(e) => setAddProfileSelection(e.target.value)}
                          />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.name}
                          </span>
                          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                          </span>
                          <span style={{ color: 'var(--color-gray)', fontSize: '12px' }}>Profile image</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">Add Bike</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Bike Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Bike</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>x</button>
            </div>
            
            <form onSubmit={handleEdit}>
              <div className="form-group">
                <label>Frame Number (cannot be changed)</label>
                <input type="text" value={formData.vin} disabled />
              </div>

              <div className="form-group">
                <label>Brand (cannot be changed)</label>
                <input
                  type="text"
                  value={formData.brand}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                  Brand is locked after bike creation
                </p>
              </div>

              <div className="form-group">
                <label>Model (cannot be changed)</label>
                <input
                  type="text"
                  value={formData.model}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                  Model is locked after bike creation
                </p>
              </div>

              <div className="form-group">
                <label>Year *</label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                  required
                  min={1900}
                  max={new Date().getFullYear() + 1}
                />
              </div>

              <div className="form-group">
                <label>Images and profile image</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => validateAndSetImages(
                    Array.from(e.target.files || []),
                    setEditImages,
                    setEditProfileSelection,
                    setEditImageError,
                    true
                  )}
                />
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '6px' }}>
                  New images total size must be at most 20MB. Choose which image should be the profile image.
                </p>
                {editImageError && <div className="error">{editImageError}</div>}

                {editImages.length > 0 && (
                  <div style={{ marginTop: '10px', background: 'var(--bg-overlay-light)', padding: '10px', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '8px' }}>
                      New images to upload: {(getFilesTotalSize(editImages) / (1024 * 1024)).toFixed(2)} MB / 20 MB
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px' }}>
                      {editImages.map((file, index) => (
                        <label key={file.name + index} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#ffffff' }}>
                          <input
                            type="radio"
                            name="editProfileImage"
                            value={`new-${index}`}
                            checked={editProfileSelection === `new-${index}`}
                            onChange={(e) => setEditProfileSelection(e.target.value)}
                          />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.name}
                          </span>
                          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                          </span>
                          <span style={{ color: 'var(--color-gray)', fontSize: '12px' }}>Profile image</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '12px' }}>
                  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '6px' }}>
                    Current images
                  </p>
                  {loadingEditImages ? (
                    <div className="loading">Loading images...</div>
                  ) : editExistingImages.length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--color-gray)' }}>No images uploaded yet.</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
                      {editExistingImages.map((img) => (
                        <label key={img.id} style={{ position: 'relative', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '6px', background: 'var(--bg-overlay-light)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <input
                            type="radio"
                            name="editProfileImage"
                            value={`existing-${img.id}`}
                            checked={editProfileSelection === `existing-${img.id}`}
                            onChange={(e) => setEditProfileSelection(e.target.value)}
                            style={{ position: 'absolute', top: '6px', left: '6px' }}
                          />
                          <img
                            src={getBikeImageUrl(img, currentBike?.id)}
                            alt={img.caption || 'Bike image'}
                            style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '6px' }}
                          />
                          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                            {img.caption || 'Nincs felirat'}
                          </span>
                          {img.is_profile && (
                            <span style={{ fontSize: '11px', color: '#3d5a20' }}>Current profile image</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">Update Bike</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Service History Modal */}
      {showServiceHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowServiceHistoryModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Service history - {currentBike?.brand} {currentBike?.model} ({currentBike?.vin})</h2>
              <button className="close-btn" onClick={() => setShowServiceHistoryModal(false)}>x</button>
            </div>
            
            <div className="modal-content">
              {loadingServices ? (
                <div className="loading">Loading service history...</div>
              ) : serviceHistory.length === 0 ? (
                <div className="no-data">
                  <p>No services recorded for this bike yet.</p>
                </div>
              ) : (
                <div className="service-history-list">
                  {serviceHistory.map((service, index) => (
                    <div key={service.id} className="service-history-item">
                      <div className="service-header">
                        <h3>Service #{serviceHistory.length - index}</h3>
                        <span className="service-date">
                          {service.service_date ? (() => {
                            try {
                              return new Date(service.service_date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              });
                            } catch (e) {
                              return service.service_date;
                            }
                          })() : 'No date'}
                        </span>
                      </div>
                      
                      <div className="service-details">
                        <div className="service-info">
                          <p><strong>Description:</strong></p>
                          <p>{service.description || 'No description'}</p>
                        </div>
                        
                        <div className="service-cost">
                          <strong>Total cost:</strong> &pound;{service.total_cost.toFixed(2)}
                        </div>
                        
                        {service.items && service.items.length > 0 && (
                          <div className="service-items">
                            <strong>Items:</strong>
                            <table className="items-table">
                              <thead>
                                <tr>
                                  <th>Item</th>
                                  <th>Qty</th>
                                  <th>Unit price</th>
                                  <th>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {service.items.map((item, idx) => (
                                  <tr key={idx}>
                                    <td>{item.item_name}</td>
                                    <td>{item.quantity}</td>
                                    <td>&pound;{item.unit_price.toFixed(2)}</td>
                                    <td>&pound;{item.total_price.toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        
                        {service.receipts && service.receipts.length > 0 && (
                          <div className="service-receipts">
                            <strong>Receipts:</strong>
                            <ul>
                              {service.receipts.map((receipt, idx) => (
                                <li key={idx}>
                                  <a href={`/api/user/receipts/${receipt.filename}`} target="_blank" rel="noopener noreferrer">
                                    {receipt.original_filename || receipt.filename}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        <div className="service-meta">
                          <small>
                            {service.company_name && (
                              <span style={{ display: 'block', marginBottom: '4px' }}>
                                <strong>Shop:</strong> {service.company_name}
                              </span>
                            )}
                            {service.technician_name && (
                              <span style={{ display: 'block', marginBottom: '4px' }}>
                                <Icon name="technician" color="secondary" darkMode={true} size={13} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                <strong>Technician:</strong> {service.technician_name}
                              </span>
                            )}
                            {service.technician_signature && (
                              <span style={{ display: 'block', marginBottom: '4px', fontStyle: 'italic', color: 'rgba(255,255,255,0.75)' }}>
                                <Icon name="write" color="secondary" darkMode={true} size={13} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                {service.technician_signature}
                              </span>
                            )}
                            {service.latest_comment?.comment_text && (
                              <span style={{ display: 'block', marginBottom: '4px', color: 'rgba(255,255,255,0.75)' }}>
                                <Icon name="chat" color="secondary" darkMode={true} size={13} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                {service.latest_comment.comment_text}
                              </span>
                            )}
                            Recorded by: {service.added_by_username} | {new Date(service.created_at).toLocaleString('en-US')}
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowServiceHistoryModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Bike Modal */}
      {showBlockModal && (
        <div className="modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Icon name="lock" color="error" darkMode={true} size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />Block Bike</h2>
              <button className="close-btn" onClick={() => setShowBlockModal(false)}>x</button>
            </div>
            
            <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-overlay-medium)', borderRadius: '8px' }}>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)' }}>
                <strong>{currentBike?.brand} {currentBike?.model}</strong>
              </p>
              <p style={{ margin: '4px 0 0 0', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                Frame: {currentBike?.vin}
              </p>
            </div>

            <div style={{ 
              marginBottom: '16px', 
              padding: '12px', 
              background: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '8px' 
            }}>
              <p style={{ margin: 0, color: '#f87171', fontSize: '13px' }}>
                <Icon name="warning" color="error" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                Blocking this bike will automatically create a support ticket for review.
              </p>
            </div>
            
            <form onSubmit={handleBlockBike}>
              <div className="form-group">
                <label>Block reason *</label>
                <textarea
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  required
                  rows={4}
                  placeholder="e.g. No-show for appointments, no response to contact, etc."
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-danger">
                  <Icon name="lock" color="error" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Block Bike
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowBlockModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bike Passport Modal - full bike page in popup iframe (same as user view) */}
      {showPassportModal && passportBike && user && (
        <BikePassportModal
          bikeId={passportBike.id}
          bikeLabel={[passportBike.brand, passportBike.model].filter(Boolean).join(' ') || undefined}
          onClose={() => { setShowPassportModal(false); setPassportBike(null); }}
          darkMode={darkMode}
          user={user}
          onLogout={onLogout}
          onToggleDarkMode={onToggleDarkMode}
        />
      )}

      {/* Bike Detail Modal */}
      {showDetailModal && currentBike && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {bikeImages.find(img => img.is_profile) && (
                  <img
                    src={getBikeImageUrl(bikeImages.find(img => img.is_profile), currentBike.id)}
                    alt="Profile"
                    style={{ width: '60px', height: '60px', borderRadius: '12px', objectFit: 'cover' }}
                  />
                )}
                <div>
                  <h2 style={{ margin: 0 }}>{currentBike?.brand} {currentBike?.model}</h2>
                  <p style={{ margin: '4px 0 0 0', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                    {currentBike?.year} &bull; Frame: {currentBike?.vin}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowDetailModal(false);
                    openPassportModal(currentBike);
                  }}
                  style={{
                    marginLeft: 'auto',
                    padding: '8px 14px',
                    background: 'rgba(61, 90, 32, 0.2)',
                    border: '1px solid rgba(61, 90, 32, 0.4)',
                    borderRadius: '8px',
                    color: '#d9f99d',
                    fontSize: '13px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  <Icon name="badge" color="primary" darkMode={true} size={14} />
                  View bike passport
                </button>
              </div>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>x</button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '8px', padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              {[
                { id: 'info', label: 'Info' },
                { id: 'images', label: `Images (${bikeImages.length})` },
                { id: 'comments', label: `Comments (${bikeComments.length})` }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setDetailTab(tab.id)}
                  style={{
                    padding: '8px 16px',
                    background: detailTab === tab.id ? 'rgba(61, 90, 32, 0.15)' : 'transparent',
                    border: detailTab === tab.id ? '1px solid rgba(61, 90, 32, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: detailTab === tab.id ? '#d9f99d' : 'rgba(255,255,255,0.8)',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="modal-content" style={{ padding: '16px' }}>
              {loadingDetail ? (
                <div className="loading">Loading...</div>
              ) : (
                <>
                  {/* Info Tab */}
                  {detailTab === 'info' && (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div style={{ padding: '16px', background: 'var(--bg-overlay-light)', borderRadius: '12px' }}>
                          <h4 style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.6)', fontSize: '12px', textTransform: 'uppercase' }}>Owner</h4>
                          <p style={{ margin: 0, color: '#ffffff' }}>
                            {currentBike.owner_firstname} {currentBike.owner_surname}
                          </p>
                        </div>
                        <div style={{ padding: '16px', background: 'var(--bg-overlay-light)', borderRadius: '12px' }}>
                          <h4 style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.6)', fontSize: '12px', textTransform: 'uppercase' }}>Services</h4>
                          <p 
                            onClick={() => {
                              setDetailTab('services');
                              viewServiceHistory(currentBike);
                            }}
                            style={{ 
                              margin: 0, 
                              color: '#4ecdc4', 
                              fontSize: '24px', 
                              fontWeight: '600',
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                            title="Click to view service history"
                          >
                            {currentBike.service_count || 0}
                          </p>
                        </div>
                      </div>
                      {currentBike.notes && (
                        <div style={{ marginTop: '16px', padding: '16px', background: 'var(--bg-overlay-light)', borderRadius: '12px' }}>
                          <h4 style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.6)', fontSize: '12px', textTransform: 'uppercase' }}>Jegyzetek</h4>
                          <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', whiteSpace: 'pre-wrap' }}>
                            {currentBike.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Images Tab */}
                  {detailTab === 'images' && (
                    <div>
                      <div style={{ marginBottom: '16px' }}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="btn btn-primary btn-sm"
                        >
                          {uploadingImage ? (
                            <>
                              <Icon name="loading" color="primary" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Icon name="upload" color="primary" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                              Upload image
                            </>
                          )}
                        </button>
                      </div>
                      
                      {bikeImages.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' }}>
                          <div style={{ marginBottom: '12px', opacity: 0.3 }}>
                            <Icon name="photos" color="muted" darkMode={true} size={48} />
                          </div>
                          <p>No images yet</p>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                          {bikeImages.map(image => (
                            <div
                              key={image.id}
                              style={{
                                position: 'relative',
                                aspectRatio: '1',
                                borderRadius: '10px',
                                overflow: 'hidden',
                                border: image.is_profile ? '2px solid #3d5a20' : '1px solid rgba(255,255,255,0.1)'
                              }}
                            >
                              <img
                                src={getBikeImageUrl(image, currentBike.id)}
                                alt={image.caption || 'Bike image'}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              {image.is_profile && (
                                <div style={{
                                  position: 'absolute',
                                  top: '4px',
                                  left: '4px',
                                  background: 'var(--primary)',
                                  color: '#000',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '9px',
                                  fontWeight: '600'
                                }}>
                                  PROFIL
                                </div>
                              )}
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                                padding: '8px',
                                display: 'flex',
                                gap: '4px'
                              }}>
                                {!image.is_profile && (
                                  <button
                                    onClick={() => handleSetProfileImage(image.id)}
                                    style={{
                                      padding: '4px 6px',
                                      background: 'rgba(255,255,255,0.2)',
                                      border: 'none',
                                      borderRadius: '4px',
                                      color: '#ffffff',
                                      fontSize: '10px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Icon name="star" color="primary" darkMode={true} size={12} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteImage(image.id)}
                                  style={{
                                    padding: '4px 6px',
                                    background: 'rgba(239, 68, 68, 0.3)',
                                    border: 'none',
                                    borderRadius: '4px',
                                    color: '#f87171',
                                    fontSize: '10px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Icon name="delete" color="error" darkMode={true} size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Comments Tab */}
                  {detailTab === 'comments' && (
                    <div>
                      {/* Add comment form */}
                      <form onSubmit={handleAddComment} style={{ marginBottom: '20px' }}>
                        <textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Write a comment..."
                          rows={3}
                          maxLength={5000}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            background: 'var(--bg-overlay-medium)',
                            color: '#ffffff',
                            fontSize: '14px',
                            marginBottom: '10px',
                            resize: 'vertical'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <select
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid rgba(255,255,255,0.1)',
                              background: 'var(--bg-overlay-medium)',
                              color: '#ffffff',
                              fontSize: '13px'
                            }}
                          >
                            {Object.entries(getCategories(true)).map(([key, { label }]) => (
                              <option key={key} value={key} style={{ background: '#1a1a3e' }}>
                                {label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="submit"
                            disabled={submittingComment || !newComment.trim()}
                            className="btn btn-primary btn-sm"
                          >
                            {submittingComment ? (
                              <Icon name="loading" color="primary" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                            ) : (
                              <>
                                <Icon name="edit" color="primary" darkMode={true} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                                Comment
                              </>
                            )}
                          </button>
                        </div>
                      </form>

                      {/* Comments list */}
                      {bikeComments.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.4)' }}>
                          <div style={{ marginBottom: '12px', opacity: 0.3 }}>
                            <Icon name="chat" color="muted" darkMode={true} size={48} />
                          </div>
                          <p>No comments yet</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {bikeComments.map(comment => {
                            const categories = getCategories(true);
                            const cat = categories[comment.category] || categories.general;
                            return (
                              <div
                                key={comment.id}
                                style={{
                                  padding: '14px',
                                  background: comment.is_pinned ? 'rgba(61, 90, 32, 0.05)' : 'rgba(255,255,255,0.02)',
                                  border: comment.is_pinned ? '1px solid rgba(61, 90, 32, 0.2)' : '1px solid rgba(255,255,255,0.05)',
                                  borderRadius: '10px'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{
                                      padding: '3px 8px',
                                      background: 'rgba(255,255,255,0.1)',
                                      borderRadius: '8px',
                                      fontSize: '11px'
                                    }}>
                                      <Icon name={cat.iconName} color="secondary" darkMode={true} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                              {cat.label}
                                    </span>
                                    {comment.is_pinned && (
                                      <span style={{ color: '#3d5a20', fontSize: '11px' }}>Pinned</span>
                                    )}
                                  </div>
                                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                                    {new Date(comment.created_at).toLocaleDateString('en-GB')}
                                  </span>
                                </div>
                                <p style={{ margin: '0 0 8px 0', color: 'rgba(255,255,255,0.85)', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                                  {comment.comment_text}
                                </p>
                                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                                  {comment.author_name || comment.author_username}
                                  {comment.company_name && (
                                    <span style={{
                                      marginLeft: '8px',
                                      padding: '2px 6px',
                                      background: 'rgba(102, 126, 234, 0.15)',
                                      color: 'rgba(102, 126, 234, 0.9)',
                                      borderRadius: '4px'
                                    }}>
                                      {comment.company_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminBikes;
